import { toast } from "sonner";
import { useContext } from "react";
import { CrmProvider, AuthMethod, CrmFormData } from "../types";
import AuthMethodSelector from "../AuthMethodSelector";
import { trpc } from "../../../trpc";
import MerchantContext from "../../../contexts/MerchantContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";

type SimiAuthConfigurationProps = {
  formData: CrmFormData;
  onFormDataChange: (data: Partial<CrmFormData>) => void;
  hasConnection: boolean;
  crmConnection: any;
};

const SimiAuthConfiguration = ({
  formData,
  onFormDataChange,
  hasConnection,
  crmConnection,
}: SimiAuthConfigurationProps): JSX.Element => {
  const { merchantId } = useContext(MerchantContext);

  const utils = trpc.useUtils();
  const createConnection = trpc.crmSelfConfig.create.useMutation({
    onSuccess: async () => {
      await utils.crmSelfConfig.get.invalidate({ merchantId: merchantId || "" });
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrio un error al crear la conexion.",
      });
    },
  });

  const testConnection = trpc.crmSelfConfig.testConnection.useMutation({
    onSuccess: async (result) => {
      if (result.success) {
        const clientId = formData.clientId || crmConnection?.simi?.clientId;
        const clientSecret = formData.clientSecret || crmConnection?.simi?.clientSecret;
        const scope = formData.scope || crmConnection?.simi?.scope;
        const inmobiliaria = formData.inmobiliaria || crmConnection?.simi?.inmobiliaria?.toString();

        if (merchantId && clientId && clientSecret && scope && inmobiliaria) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          await createConnection.mutateAsync({
            merchantId,
            provider: "simi" as CrmProvider,
            simi: {
              clientId,
              clientSecret,
              scope,
              inmobiliaria: Number(inmobiliaria),
              accessToken: result.accessToken,
            },
            status: "success",
            enabled: true,
          } as any);
        }

        toast.success("Conexion exitosa", {
          description: "La conexion con Simi se ha establecido correctamente.",
        });
      } else {
        toast.error("Error de conexion", {
          description: result.error || "No se pudo establecer la conexion con Simi.",
        });
      }
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "Ocurrio un error al probar la conexion.",
      });
    },
  });

  const handleTestConnection = () => {
    const clientId = (formData.clientId || crmConnection?.simi?.clientId || "").trim();
    const clientSecret = (formData.clientSecret || crmConnection?.simi?.clientSecret || "").trim();
    const scope = (formData.scope || crmConnection?.simi?.scope || "").trim();
    const inmobiliaria = (formData.inmobiliaria || crmConnection?.simi?.inmobiliaria?.toString() || "").trim();

    if (!clientId || !clientSecret || !scope || !inmobiliaria) {
      toast.warning("Credenciales requeridas", {
        description:
          "Por favor ingresa todos los campos (Client ID, Client Secret, Scope e Inmobiliaria) antes de probar la conexion.",
      });
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    testConnection.mutate({
      provider: "simi" as CrmProvider,
      clientId,
      clientSecret,
      scope,
      inmobiliaria: Number(inmobiliaria),
    } as any);
  };

  const handleAuthMethodChange = (method: AuthMethod) => {
    onFormDataChange({
      authMethod: method,
      clientId: formData.authMethod === method ? formData.clientId : "",
      clientSecret: formData.authMethod === method ? formData.clientSecret : "",
      scope: formData.authMethod === method ? formData.scope : "",
      inmobiliaria: formData.authMethod === method ? formData.inmobiliaria : "",
    });
  };

  const handleClientIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFormDataChange({ clientId: e.target.value });
  };

  const handleClientSecretChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFormDataChange({ clientSecret: e.target.value });
  };

  const handleScopeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFormDataChange({ scope: e.target.value });
  };

  const handleInmobiliariaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFormDataChange({ inmobiliaria: e.target.value });
  };

  const isConnected = hasConnection && crmConnection.status === "success";

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex flex-col gap-4">
        <div>
          <div className="mb-3 flex items-center justify-between">
            <Label className="text-lg font-bold text-gray-700">
              Metodo de autenticacion
            </Label>
            {isConnected && (
              <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                Conexion exitosa
              </span>
            )}
          </div>
          <AuthMethodSelector
            methods={["oauth"]}
            selectedMethod={isConnected ? "oauth" : formData.authMethod || "oauth"}
            onSelect={handleAuthMethodChange}
            isDisabled={isConnected}
          />
        </div>

        <div>
          <Label className="text-sm font-semibold text-gray-700">
            Client ID
          </Label>
          <Input
            type="text"
            value={
              isConnected && crmConnection.simi?.clientId
                ? "••••••••••••"
                : formData.clientId || crmConnection?.simi?.clientId || ""
            }
            onChange={handleClientIdChange}
            placeholder="Ingresa el Client ID"
            className="bg-gray-50 hover:border-gray-300 focus:border-purple-500 focus:bg-white"
            disabled={isConnected}
          />
        </div>

        <div>
          <Label className="text-sm font-semibold text-gray-700">
            Client Secret
          </Label>
          <Input
            type="password"
            value={
              isConnected && crmConnection.simi?.clientSecret
                ? "••••••••••••"
                : formData.clientSecret || crmConnection?.simi?.clientSecret || ""
            }
            onChange={handleClientSecretChange}
            placeholder="Ingresa el Client Secret"
            className="bg-gray-50 hover:border-gray-300 focus:border-purple-500 focus:bg-white"
            disabled={isConnected}
          />
        </div>

        <div>
          <Label className="text-sm font-semibold text-gray-700">
            Scope
          </Label>
          <Input
            type="text"
            value={
              isConnected && crmConnection.simi?.scope
                ? "••••••••••••"
                : formData.scope || crmConnection?.simi?.scope || ""
            }
            onChange={handleScopeChange}
            placeholder="Ingresa el Scope (ej: services)"
            className="bg-gray-50 hover:border-gray-300 focus:border-purple-500 focus:bg-white"
            disabled={isConnected}
          />
        </div>

        <div>
          <Label className="text-sm font-semibold text-gray-700">
            Inmobiliaria
          </Label>
          <Input
            type="number"
            value={
              isConnected && crmConnection.simi?.inmobiliaria
                ? crmConnection.simi.inmobiliaria.toString()
                : formData.inmobiliaria || crmConnection?.simi?.inmobiliaria?.toString() || ""
            }
            onChange={handleInmobiliariaChange}
            placeholder="Codigo de inmobiliaria"
            className="bg-gray-50 hover:border-gray-300 focus:border-purple-500 focus:bg-white"
            disabled={isConnected}
          />
        </div>

        {!isConnected && (
          <Button
            variant="main"
            onClick={handleTestConnection}
            disabled={
              testConnection.isLoading ||
              (!formData.clientId && !crmConnection?.simi?.clientId) ||
              (!formData.clientSecret && !crmConnection?.simi?.clientSecret) ||
              (!formData.scope && !crmConnection?.simi?.scope) ||
              (!formData.inmobiliaria && !crmConnection?.simi?.inmobiliaria)
            }
          >
            {testConnection.isLoading && <Loader2 className="size-4 animate-spin" />}
            Probar conexion
          </Button>
        )}
      </div>
    </div>
  );
};

export default SimiAuthConfiguration;
